import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useStore } from "@/hooks/use-stores";
import { supabase } from "@/lib/supabase";
import { formatDate } from "@/lib/utils";
import type { SyncedProduct } from "@/types";
import { ArrowLeft, Copy, ExternalLink, RefreshCw, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";

export function StoreDetailPage() {
	const { id } = useParams<{ id: string }>();
	const { store, loading, error } = useStore(id);
	const [products, setProducts] = useState<SyncedProduct[]>([]);
	const [productsLoading, setProductsLoading] = useState(true);
	const [syncing, setSyncing] = useState(false);
	const [showVerify, setShowVerify] = useState(false);
	const [sending, setSending] = useState(false);
	const [verifying, setVerifying] = useState(false);
	const [code, setCode] = useState("");
    // errorMessage intentionally omitted; errors are surfaced via alert dialogs if needed
	const { session } = useAuth();

	useEffect(() => {
		if (!id) return;
		async function fetchProducts() {
			const { data } = await supabase
				.from("synced_products")
				.select("*")
				.eq("store_id", id)
				.order("title");
			setProducts(data ?? []);
			setProductsLoading(false);
		}
		fetchProducts();
	}, [id]);

	async function handleSync() {
		if (!id) return;
		setSyncing(true);
		try {
			await fetch("/api/sync-products", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ store_id: id }),
			});
			// Refetch products
			const { data } = await supabase
				.from("synced_products")
				.select("*")
				.eq("store_id", id)
				.order("title");
			setProducts(data ?? []);
		} finally {
			setSyncing(false);
		}
	}

	async function handleDelete() {
		// Start the verification flow by requesting a code be sent
		if (!(id && store && session?.access_token)) return;
        // clear previous errors if any
		setSending(true);
		try {
			const res = await fetch("/api/delete-store", {
				method: "POST",
				headers: {
					Authorization: `Bearer ${session.access_token}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ action: "send_code", store_id: id }),
			});
			if (!res.ok) throw new Error(await getErrorMessage(res, "Failed to request verification code"));
			setShowVerify(true);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            alert(msg);
        } finally {
            setSending(false);
        }
    }

	async function handleVerifyAndDelete() {
		if (!(id && store && session?.access_token)) return;
        // clear previous errors if any
		setVerifying(true);
		try {
			const res = await fetch("/api/delete-store", {
				method: "POST",
				headers: {
					Authorization: `Bearer ${session.access_token}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ action: "verify_and_delete", store_id: id, code }),
			});
			if (!res.ok) throw new Error(await getErrorMessage(res, "Failed to verify code"));
			// Success — navigate back to stores
			window.location.href = "/stores";
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            alert(msg);
        } finally {
            setVerifying(false);
        }
    }

	if (loading) {
		return (
			<div className="flex items-center justify-center py-20 text-muted-foreground">
				Loading store...
			</div>
		);
	}

	if (error || !store) {
		return (
			<div className="space-y-4">
				<Link
					to="/stores"
					className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
				>
					<ArrowLeft className="h-4 w-4" /> Back to Stores
				</Link>
				<p className="text-destructive">Store not found.</p>
			</div>
		);
	}

	return (
		<div className="space-y-8">
			<div className="flex items-center gap-4">
				<Link to="/stores" className="text-muted-foreground hover:text-foreground">
					<ArrowLeft className="h-5 w-5" />
				</Link>
				<div className="flex-1">
					<div className="flex items-center gap-3">
						<h1 className="text-3xl font-bold tracking-tight">{store.name}</h1>
						<Badge
							variant={
								store.status === "active"
									? "success"
									: store.status === "paused"
										? "warning"
										: "destructive"
							}
						>
							{store.status}
						</Badge>
					</div>
					<a
						href={`https://${store.shopify_domain}`}
						target="_blank"
						rel="noopener noreferrer"
						className="text-sm text-muted-foreground hover:underline inline-flex items-center gap-1"
					>
						{store.shopify_domain}
						<ExternalLink className="h-3 w-3" />
					</a>
				</div>
                <div className="flex items-center gap-2">
                    <Button variant="destructive" size="sm" onClick={handleDelete} disabled={sending}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                    </Button>
                    {showVerify && (
                        <div className="flex items-center gap-2">
                            <input
                                className="rounded-md border px-2 py-1 text-sm"
                                placeholder="6-digit code"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                            />
                            <Button size="sm" onClick={handleVerifyAndDelete} disabled={verifying || !code}>
                                {verifying ? "Verifying..." : "Verify & Delete"}
                            </Button>
                        </div>
                    )}
                </div>
			</div>

			{/* Email Configuration */}
			<Card>
				<CardHeader>
					<CardTitle className="text-base">Email Configuration</CardTitle>
					<CardDescription>
						Share this email address with your customers. Zoho Mail webhooks will deliver incoming
						messages here, and replies will use your custom alias when available.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="rounded-md border p-3 space-y-2">
						<div>
							<p className="text-sm font-medium">Zoho Mail</p>
							<p className="text-xs text-muted-foreground">
								Sunmerce requests a Zoho access token directly from the self-client using the
								server-side client credentials.
							</p>
						</div>
						<p className="text-xs text-muted-foreground">
							Set `ZOHO_CLIENT_ID` and `ZOHO_CLIENT_SECRET` in the server environment. Replies use
							`custom_email` when present, otherwise `generated_email`.
						</p>
					</div>
					<div>
						<p className="text-xs font-medium text-muted-foreground mb-1">
							Generated Email (always active)
						</p>
						<div className="flex items-center gap-2">
							<code className="rounded bg-muted px-2 py-1 text-sm">{store.generated_email}</code>
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7"
								onClick={() => navigator.clipboard.writeText(store.generated_email)}
							>
								<Copy className="h-3 w-3" />
							</Button>
						</div>
					</div>
					{store.custom_email && (
						<div>
							<p className="text-xs font-medium text-muted-foreground mb-1">
								Custom Email Alias
							</p>
							<code className="rounded bg-muted px-2 py-1 text-sm">{store.custom_email}</code>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Synced Products */}
			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle className="text-base">Synced Products</CardTitle>
							<CardDescription>{products.length} products synced from Shopify</CardDescription>
						</div>
						<Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}>
							<RefreshCw className={`mr-2 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
							{syncing ? "Syncing..." : "Sync Now"}
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					{productsLoading ? (
						<p className="text-sm text-muted-foreground">Loading products...</p>
					) : products.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							No products synced yet. Click "Sync Now" to pull products from Shopify.
						</p>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Product</TableHead>
									<TableHead>Variants</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Last Synced</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{products.map((product) => (
									<TableRow key={product.id}>
										<TableCell className="font-medium">{product.title}</TableCell>
										<TableCell>{product.variants?.length ?? 0}</TableCell>
										<TableCell>
											<Badge variant={product.status === "active" ? "success" : "secondary"}>
												{product.status}
											</Badge>
										</TableCell>
										<TableCell className="text-muted-foreground">
											{formatDate(product.synced_at)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>
		</div>
	);
}

async function getErrorMessage(res: Response, fallback: string) {
    const contentType = res.headers.get("Content-Type") ?? "";

    if (contentType.includes("application/json")) {
        const body = await res.json();
        return body.error?.message ?? fallback;
    }

    const text = await res.text();
    return text || fallback;
}
